import React, { useState, useEffect, useCallback } from 'react';
import { formatDate, formatRelative } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import { tenantApi, IngestionIntegrationHealth, IngestionFailure } from '../services/tenantApi';
import './IngestionHealthWidget.css';

interface IngestionHealthWidgetProps {
  /** If true, renders a compact version suitable for sidebar placement */
  compact?: boolean;
}

export const IngestionHealthWidget: React.FC<IngestionHealthWidgetProps> = ({ compact = false }) => {
  useDateTimePreferences();
  const [integrations, setIntegrations] = useState<IngestionIntegrationHealth[]>([]);
  const [overallStatus, setOverallStatus] = useState<string>('healthy');
  const [failures, setFailures] = useState<IngestionFailure[]>([]);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    try {
      setError(null);
      const data = await tenantApi.getIngestionHealth();
      setIntegrations(data.integrations || []);
      setOverallStatus(data.overallStatus || 'healthy');
    } catch (err) {
      setError('Unable to load ingestion health');
      console.error('Failed to load ingestion health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadHealth, 60_000);
    return () => clearInterval(interval);
  }, [loadHealth]);

  const handleShowDetail = async () => {
    if (!showDetail) {
      try {
        const data = await tenantApi.getIngestionFailures();
        setFailures(data.failures || []);
      } catch {
        setFailures([]);
      }
    }
    setShowDetail(!showDetail);
  };

  const statusColorClass = (status: string) => {
    switch (status) {
      case 'healthy': return 'ih-status-healthy';
      case 'degraded': return 'ih-status-degraded';
      case 'warning': return 'ih-status-warning';
      case 'critical': return 'ih-status-critical';
      default: return 'ih-status-healthy';
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'healthy': return 'Healthy';
      case 'degraded': return 'Degraded';
      case 'warning': return 'Warning';
      case 'critical': return 'Critical';
      default: return 'Unknown';
    }
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return 'Never';
    return Math.abs(Date.now() - new Date(iso).getTime()) < 86400000 ? formatRelative(iso) : formatDate(iso);
  };

  if (loading) {
    return (
      <div className="ih-widget">
        <div className="ih-loading">Loading ingestion status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ih-widget ih-status-degraded">
        <div className="ih-header">
          <span className="ih-title">Ingestion Health</span>
        </div>
        <div className="ih-error">{error}</div>
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <div className="ih-widget ih-status-healthy">
        <div className="ih-header">
          <span className="ih-title">Ingestion Health</span>
          <span className="ih-badge ih-status-healthy">No Integrations</span>
        </div>
        <p className="ih-empty">No webhook integrations configured.</p>
      </div>
    );
  }

  return (
    <div className={`ih-widget ${statusColorClass(overallStatus)}`}>
      <div className="ih-header" onClick={handleShowDetail} style={{ cursor: 'pointer' }}>
        <span className="ih-title">Ingestion Health</span>
        <span className={`ih-badge ${statusColorClass(overallStatus)}`}>
          {statusLabel(overallStatus)}
        </span>
      </div>

      {compact ? (
        /* Compact mode: just overall status + per-integration status dots */
        <div className="ih-compact-list">
          {integrations.map((ig) => (
            <div key={ig.integrationId} className="ih-compact-row">
              <span className={`ih-dot ${statusColorClass(ig.status)}`} />
              <span className="ih-compact-name" title={ig.integrationName}>
                {ig.integrationName}
              </span>
              <span className="ih-compact-stats">
                {ig.failures1h > 0 ? `${ig.failures1h} fail` : 'OK'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        /* Full mode: detailed table */
        <div className="ih-table-wrap">
          <table className="ih-table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Status</th>
                <th>Failures (1h)</th>
                <th>Failures (24h)</th>
                <th>Last Success</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((ig) => (
                <tr key={ig.integrationId} className={statusColorClass(ig.status)}>
                  <td>{ig.integrationName}</td>
                  <td>
                    <span className={`ih-badge-sm ${statusColorClass(ig.status)}`}>
                      {statusLabel(ig.status)}
                    </span>
                  </td>
                  <td className="ih-num">{ig.failures1h}</td>
                  <td className="ih-num">{ig.failures24h}</td>
                  <td>{formatTime(ig.lastSuccess)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel: recent failures */}
      {showDetail && failures.length > 0 && (
        <div className="ih-detail">
          <h4>Recent Failures</h4>
          <div className="ih-failures-list">
            {failures.map((f) => (
              <div key={f.attemptId} className="ih-failure-row">
                <span className="ih-failure-time">{formatTime(f.attemptedAt)}</span>
                <span className="ih-failure-reason">{f.failureReason}</span>
                <span className="ih-failure-ip">{f.sourceIp}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showDetail && failures.length === 0 && (
        <div className="ih-detail">
          <p className="ih-empty">No recent failures.</p>
        </div>
      )}
    </div>
  );
};
